/**
 * Fix S / S6 — a draw offer you cannot miss. The old screen showed it in an
 * overlay above every panel; V2 regressed it into the Moves-only
 * `EventDock`. This floats the SAME card over whichever panel is open
 * everywhere else, so leaving the Moves tab can never hide an offer you
 * have to answer.
 *
 * Visible only while a draw offer is pending for me (`isDrawOfferPending`,
 * `../dockEvents` — the SAME rule `EventDock` itself uses, not a second
 * one) AND the rail is NOT on Moves — `EventDock` keeps the card there, so
 * the offer never renders twice.
 *
 * No `model.ts`: the one thing this unit decides (`visible`) is one
 * `useShallow` read plus one `useV2Ui` read, the same shape
 * `rail/NoticeCard` already keeps as a two-file unit.
 *
 * T0.2 (Fix S review #2): the `useShallow` read used to be `selectDockInput`
 * wholesale, which also carries `moves: s.game.moves.length` for the Moves
 * tab's first-move countdown — a re-render per opponent move, on EVERY tab,
 * from a unit `rail/Rail/View.tsx` mounts unconditionally. Narrowed to
 * exactly the two fields this unit (and `DrawOfferCard`) actually need.
 */
import React from 'react';
import {useShallow} from 'zustand/react/shallow';

import {useLiveGame} from '@/Store/liveGameStore';
import type {LiveGameState} from '@/Store/liveGameStore';

import {bump} from '../../../stage/renderCount';
import {isDrawOfferPending} from '../dockEvents';
import {useV2Ui} from '../../../theme/useV2Ui';
import FloatingDrawOfferView from './View';

type FloatingDrawOfferInput = {
  drawOffered: boolean;
  opponentName: string;
};

const selectFloatingDrawOfferInput = (
  s: LiveGameState,
): FloatingDrawOfferInput => ({
  drawOffered: s.game.drawOffered,
  opponentName: s.game.opponentName,
});

export default React.memo(function FloatingDrawOffer() {
  bump('FloatingDrawOffer');
  const input = useLiveGame(useShallow(selectFloatingDrawOfferInput));
  const railTab = useV2Ui(s => s.railTab);
  const visible = isDrawOfferPending(input) && railTab !== 'moves';

  return (
    <FloatingDrawOfferView
      visible={visible}
      opponentName={input.opponentName}
    />
  );
});
